import { useContext } from 'react';
import ViewportContext from '../ViewportProvider/ViewportContext';

/**
 * L'area PRINCIPALE visibile per l'utente che ha come breakpoint 1920x1080px.
 */
export const MAIN = Object.freeze({ width: 1920, height: 1080, name: 'main' });

/**
 * L'area media visibile per l'utente che ha come breakpoint 1600x900px.
 */
export const MEDIUM = Object.freeze({ width: 1600, height: 900, name: 'medium' });

/**
 * L'area visibile per l'utente a risoluzine BASSA. E' pensata per una risoluzione 1280X768px ma di fatto non ha un breakpoint.
 */
export const LOW = Object.freeze({ width: 0, height: 0, name: 'low' });

export const LOW_MAIN_RESOLUTIONS = [LOW, MAIN];
export const LOW_MEDIUM_MAIN_RESOLUTIONS = [LOW, MEDIUM, MAIN];

export const RESPONSIVE_MODE_WIDTH = 'width';
export const RESPONSIVE_MODE_HEIGHT = 'height';
export const RESPONSIVE_MODE_ANY = 'any';

/**
 * Registra un nuova risoluzione.
 * Ogni risoluzione ha un name che la identifica e un breakpoint in lunghezza e in altezza.
 *
 * La risulzione inserita delimita un'area tra il suo breakpoint (di norma quello in lunghezza) e il breakpoint della risoluzione successiva.
 * @param {string} name nome della risoluzione.
 * @param {number} width dimensione del brakpoint in larghezza
 * @param {number} height dimensione del brakpoint in lunghezza
 * @returns {object} la nuova risoluzione.
 */
export function registerResolution(name, width, height) {
  return { name, width, height };
}

/**
 * Ritorna la resolution.
 * @param {string} name ritorna la resolution per nome.
 * @param {array} resolutions lista delle risoluzioni [il default è LOW_MAIN_RESOLUTIONS].
 * @returns {object | undefined} il viewport (resolution, width, height).
 */
export function getResolutionByName(name, resolutions = LOW_MAIN_RESOLUTIONS) {
  return resolutions.find((r) => r.name === name);
}

/**
 * Ritorna la resolution in base ad una certa dimensione.
 * @param {RESPONSIVE_MODE_WIDTH | RESPONSIVE_MODE_HEIGHT | RESPONSIVE_MODE_ANY}
 * mode la modalità di applicazione del breakpoint [default RESPONSIVE_MODE_WIDTH].
 * @param {number} width larghezza dell'area disponibile del browser
 * @param {number} height altezza dell'area disponibile del browser
 * @param {array} resolutions lista delle risoluzioni [il default è LOW_MAIN_RESOLUTIONS].
 * @returns {object} il viewport corrente (resolution, width, height).
 */
export function getResolution(mode = RESPONSIVE_MODE_WIDTH, width = window.innerWidth, height = window.innerHeight, resolutions = LOW_MAIN_RESOLUTIONS) {
  switch (mode) {
    case RESPONSIVE_MODE_WIDTH: {
      return resolutions.reduce((last, r) => (width >= r.width ? r : last), undefined);
    }
    case RESPONSIVE_MODE_HEIGHT: {
      return resolutions.reduce((last, r) => (height >= r.height ? r : last), undefined);
    }
    case RESPONSIVE_MODE_ANY:
      return resolutions.reduce((last, r) => (height >= r.height || width >= r.width ? r : last), undefined);
    default: {
      // eslint-disable-next-line no-console
      console.error(`getResolution: mode ${mode} undefined`);
      return MAIN;
    }
  }
}

/**
 * Ritorna un oggetto di tipo Viewport che cambia in base alla dimensione della viewport.
 * `useViewport` utilizza il contesto `ViewportContext` (vedi anche `ViewportProvider`).
 * @param {array} resolutions lista delle risoluzioni [il default è LOW_MAIN_RESOLUTIONS].
 * Le risoluzioni devono essere ordinate in modo crescente.
 * @param {RESPONSIVE_MODE_WIDTH | RESPONSIVE_MODE_HEIGHT | RESPONSIVE_MODE_ANY}
 * mode la modalità di applicazione del breakpoint [default RESPONSIVE_MODE_WIDTH].
 * @returns {object} il viewport corrente (resolution, width, height).
 */
function useViewport(resolutions, mode) {
  const { width, height } = useContext(ViewportContext);
  return { width, height, resolution: getResolution(mode, width, height, resolutions) };
}

export default useViewport;
